import type { DataIssue, IndicatorSeries, Observation } from "../../types";
import { csvRecords } from "../../utils/csv.ts";
import { applyFreshness } from "../freshness.ts";

export const TREASURY_SERIES = {
  ust1: { id: "ust_1y", column: "1 Yr", name: "미국 국채 1년", shortName: "UST 1Y" },
  ust2: { id: "ust_2y", column: "2 Yr", name: "미국 국채 2년", shortName: "UST 2Y" },
  ust3: { id: "ust_3y", column: "3 Yr", name: "미국 국채 3년", shortName: "UST 3Y" },
  ust5: { id: "ust_5y", column: "5 Yr", name: "미국 국채 5년", shortName: "UST 5Y" },
  ust10: { id: "ust_10y", column: "10 Yr", name: "미국 국채 10년", shortName: "UST 10Y" }
} as const;

interface TreasuryProxyResponse {
  ok: boolean;
  csv?: string;
  error?: string;
  collectedAt?: string;
  sourceUrl?: string;
}

function parseUsDate(value: string) {
  const m = value.trim().match(/^(\d{1,2})\/(\d{1,2})\/(\d{2}|\d{4})$/);
  if (!m) return null;
  const year = m[3].length === 2 ? String(2000 + Number(m[3])) : m[3];
  return `${year}-${m[1].padStart(2, "0")}-${m[2].padStart(2, "0")}`;
}

function parseNumber(value: string | undefined) {
  const text = (value ?? "").trim();
  if (!text || /^N\/?A$/i.test(text) || text === ".") return null;
  const n = Number(text.replace(/,/g, ""));
  return Number.isFinite(n) ? n : null;
}

function normalizeHeader(value: string) {
  return value.replace(/\s+/g, " ").trim().toLowerCase();
}

function findColumn(record: Record<string, string>, expected: string) {
  const target = normalizeHeader(expected);
  return Object.keys(record).find((key) => normalizeHeader(key) === target);
}

export function parseTreasuryYearCsv(csv: string) {
  const records = csvRecords(csv);
  if (!records.length) throw new Error("Treasury CSV contained no data rows");
  const dateColumn = Object.keys(records[0]).find((key) => normalizeHeader(key) === "date");
  if (!dateColumn) throw new Error("Treasury CSV missing Date column");
  const columns = Object.fromEntries(Object.values(TREASURY_SERIES).map((spec) => {
    const found = findColumn(records[0], spec.column);
    if (!found) throw new Error(`Treasury CSV missing '${spec.column}' column`);
    return [spec.id, found];
  })) as Record<string, string>;

  return records.map((record) => ({
    date: parseUsDate(record[dateColumn]),
    values: Object.fromEntries(Object.entries(columns).map(([id, key]) => [id, parseNumber(record[key])])) as Record<string, number | null>
  })).filter((row): row is { date: string; values: Record<string, number | null> } => Boolean(row.date));
}

async function fetchTreasuryResource(query: string, signal?: AbortSignal) {
  const response = await fetch(`/api/treasury?${query}`, { headers: { Accept: "application/json" }, signal });
  const body = await response.json() as TreasuryProxyResponse;
  if (!response.ok || !body.ok || !body.csv) throw new Error(body.error || `Treasury proxy error (${response.status})`);
  return { rows: parseTreasuryYearCsv(body.csv), collectedAt: body.collectedAt, sourceUrl: body.sourceUrl };
}

async function mapPool<T, R>(items: T[], concurrency: number, fn: (item: T) => Promise<R>): Promise<R[]> {
  const out = new Array<R>(items.length);
  let cursor = 0;
  async function worker() {
    while (true) {
      const index = cursor++;
      if (index >= items.length) return;
      out[index] = await fn(items[index]);
    }
  }
  await Promise.all(Array.from({ length: Math.min(concurrency, items.length) }, worker));
  return out;
}

function unavailable(spec: typeof TREASURY_SERIES[keyof typeof TREASURY_SERIES], note: string): IndicatorSeries {
  return {
    id: spec.id,
    name: spec.name,
    shortName: spec.shortName,
    unit: "%",
    layer: "market",
    source: {
      provider: "U.S. Department of the Treasury",
      dataset: "Daily Treasury Par Yield Curve Rates",
      url: "https://home.treasury.gov/resource-center/data-chart-center/interest-rates",
      frequency: "daily",
      quality: "unavailable",
      note
    },
    observations: []
  };
}

export async function loadTreasuryYieldCurve(startYear = 2010, signal?: AbortSignal): Promise<{ series: IndicatorSeries[]; issues: DataIssue[] }> {
  const currentYear = new Date().getUTCFullYear();
  const resources: { id: string; query: string; minYear: number; maxYear: number }[] = [];
  if (startYear <= 2019) resources.push({ id: "2010-2019", query: "archive=2010-2019", minYear: 2010, maxYear: 2019 });
  if (startYear <= 2023) resources.push({ id: "2020-2023", query: "archive=2020-2023", minYear: 2020, maxYear: 2023 });
  for (let year = Math.max(2024, startYear); year <= currentYear; year++) {
    resources.push({ id: String(year), query: `year=${year}`, minYear: year, maxYear: year });
  }

  const issues: DataIssue[] = [];
  const successful: { id: string; rows: ReturnType<typeof parseTreasuryYearCsv>; collectedAt?: string; sourceUrl?: string }[] = [];
  const results = await mapPool(resources, 4, async (resource) => {
    try {
      const value = await fetchTreasuryResource(resource.query, signal);
      const rows = value.rows.filter((row) => {
        const year = Number(row.date.slice(0, 4));
        return year >= Math.max(startYear, resource.minYear) && year <= resource.maxYear;
      });
      if (!rows.length) throw new Error("Treasury CSV contained no rows in requested period");
      return { ok: true as const, resource, value: { ...value, rows } };
    } catch (error) {
      return { ok: false as const, resource, error: error instanceof Error ? error.message : String(error) };
    }
  });

  for (const result of results) {
    if (result.ok) successful.push({ id: result.resource.id, ...result.value });
    else issues.push({ id: `treasury-${result.resource.id}`, severity: "warning", scope: "us-treasury", message: `${result.resource.id}: ${result.error}` });
  }

  if (!successful.length) {
    return { series: Object.values(TREASURY_SERIES).map((spec) => unavailable(spec, "공식 Treasury CSV/Archive를 불러오지 못했습니다.")), issues };
  }

  const collectedAt = successful.map((x) => x.collectedAt).filter((x): x is string => Boolean(x)).sort().at(-1);
  const sourceUrl = "https://home.treasury.gov/resource-center/data-chart-center/interest-rates";
  const perSeries = new Map<string, Map<string, Observation>>();
  for (const spec of Object.values(TREASURY_SERIES)) perSeries.set(spec.id, new Map());
  for (const resource of successful) {
    for (const row of resource.rows) {
      for (const spec of Object.values(TREASURY_SERIES)) {
        perSeries.get(spec.id)!.set(row.date, { date: row.date, value: row.values[spec.id] ?? null });
      }
    }
  }

  const series = Object.values(TREASURY_SERIES).map((spec): IndicatorSeries => {
    const observations = Array.from(perSeries.get(spec.id)!.values()).sort((a, b) => a.date.localeCompare(b.date));
    const raw: IndicatorSeries = {
      id: spec.id,
      name: spec.name,
      shortName: spec.shortName,
      unit: "%",
      layer: "market",
      source: {
        provider: "U.S. Department of the Treasury",
        dataset: "Daily Treasury Par Yield Curve Rates",
        url: sourceUrl,
        frequency: "daily",
        quality: observations.some((o) => o.value != null) ? "official" : "unavailable",
        observationDate: [...observations].reverse().find((o) => o.value != null)?.date,
        collectedAt,
        itemCode: spec.column,
        itemName: spec.column,
        nativeUnit: "%",
        note: `Official Treasury archive CSV (2010-2023) + yearly CSV (2024-current). Requested start=${startYear}. Missing trading days/tenors remain N/A.`
      },
      observations
    };
    const freshness = applyFreshness(raw);
    if (freshness.issue) issues.push(freshness.issue);
    return freshness.series;
  });
  return { series, issues };
}
